// ===== StreamlinedChatApp.js =====
import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { getRandomQuestions } from '../../data/questionBank';
import ChatHistorySidebar from './ChatHistorySidebar';
import ReportsSidebar from './ReportsSidebar';
import SourceTracker from '../SourceTracker';
import ShareButton from './ShareButton';

import {
  PDFDownloadButton,
  MarkdownRenderer,
  ChatHeader,
  ChatInput
} from './ChatComponents';

import {
  filterStatusMessages,
  MessageRenderer,
  EmptyState,
  renderContentWithCharts  
} from './ChatMessageHandler';

const PRESIGNED_URL_API = process.env.REACT_APP_PRESIGNED_URL_API
  || 'https://vdc8y54qsf.execute-api.us-east-2.amazonaws.com/prod/get-presigned-url';

const getPresignedUrl = async (s3Key, userName, sessionId) => {
  const response = await fetch(PRESIGNED_URL_API, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ s3Key, userName, sessionId })
  });
  if (!response.ok) {
    const errData = await response.json().catch(() => ({}));
    throw new Error(errData.error || `HTTP ${response.status}`);
  }
  const data = await response.json();
  if (!data.success) throw new Error(data.error || 'Failed to get presigned URL');
  return data;
};

// Agent Status Indicator
const AgentStatusIndicator = ({ agentStatus, currentStatus }) => {
  const agentIcons = {
    'orchestrator_thinking': { icon: '🧠', color: 'from-purple-500 to-blue-600', label: 'Orchestrator' },
    'thinking': { icon: '💭', color: 'from-blue-500 to-cyan-600', label: 'Analyzing' },
    'tool_executing': { icon: '⚡', color: 'from-amber-500 to-orange-600', label: 'Sub-Agent' },
    'generating_response': { icon: '✍️', color: 'from-green-500 to-emerald-600', label: 'Writing' },
  };
  const statusInfo = agentIcons[agentStatus] || agentIcons['thinking'];
  return (
    <div className="flex items-center space-x-3 px-4 py-2.5 bg-white dark:bg-gray-700 rounded-xl shadow-sm border border-gray-200 dark:border-gray-600 max-w-md mx-auto animate-fadeIn">
      <div className={`w-8 h-8 bg-gradient-to-br ${statusInfo.color} rounded-full flex items-center justify-center animate-pulse shadow-md`}>
        <span className="text-sm">{statusInfo.icon}</span>
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium text-gray-900 dark:text-white truncate">{currentStatus || 'Processing...'}</div>
        <div className="text-xs text-gray-500 dark:text-gray-400">{statusInfo.label} is working</div>
      </div>
      <div className="flex space-x-1">
        <div className="w-1.5 h-1.5 bg-blue-500 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
        <div className="w-1.5 h-1.5 bg-blue-500 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
        <div className="w-1.5 h-1.5 bg-blue-500 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
      </div>
    </div>
  );
};

// Loading Indicator
const SimpleLoadingIndicator = ({ message, agentStatus, currentStatus }) => {
  const [showProgress, setShowProgress] = useState(false);
  const [progress, setProgress] = useState(0);
  const [statusMessage, setStatusMessage] = useState('Extracting relevant data...');
  const [currentTip, setCurrentTip] = useState('');
  const LOADING_TIPS = [
    "💡 DataWeb AI can analyze complex datasets and identify hidden patterns.",
    "🔍 Ask follow-up questions to dive deeper into specific findings.",
    "📊 Reports are automatically saved and can be downloaded as PDFs.",
    "⚡ The AI cross-references multiple data sources for comprehensive insights.",
    "🎯 Be specific in your questions for more targeted analysis.",
    "📈 You can ask for data visualizations and comparative analysis.",
    "🔄 Previous reports can be accessed from the Reports panel.",
    "💬 Natural language queries work best - just ask like you're talking to an analyst."
  ];
  useEffect(() => {
    const showTimer = setTimeout(() => setShowProgress(true), 2000);
    const startTime = Date.now();
    const targetDuration = 135000;
    let tipIndex = 0;
    setCurrentTip(LOADING_TIPS[0]);
    const progressInterval = setInterval(() => {
      const elapsed = Date.now() - startTime;
      let newProgress = elapsed < targetDuration
        ? (elapsed / targetDuration) * 95
        : 95 + (4 * (1 - Math.exp(-(elapsed - targetDuration) / 300000)));
      setProgress(Math.floor(newProgress));
      if (currentStatus) setStatusMessage(currentStatus);
      else if (newProgress >= 90) setStatusMessage('Finalizing report...');
      else if (newProgress >= 60) setStatusMessage('Writing report...');
      else if (newProgress >= 30) setStatusMessage('Analyzing data...');
      else setStatusMessage('Extracting relevant data...');
    }, 500);
    const tipInterval = setInterval(() => {
      tipIndex = (tipIndex + 1) % LOADING_TIPS.length;
      setCurrentTip(LOADING_TIPS[tipIndex]);
    }, 25000);
    return () => { clearTimeout(showTimer); clearInterval(progressInterval); clearInterval(tipInterval); };
  }, [currentStatus]);

  if (!showProgress) {
    if (agentStatus && currentStatus) return <AgentStatusIndicator agentStatus={agentStatus} currentStatus={currentStatus} />;
    return null;
  }
  return (
    <div className="space-y-3 max-w-md mx-auto">
      {agentStatus && currentStatus && <AgentStatusIndicator agentStatus={agentStatus} currentStatus={currentStatus} />}
      <div className="p-4 space-y-3 bg-white dark:bg-gray-700 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-600 animate-fadeIn">
        <div className="flex items-center space-x-3">
          <div className="w-6 h-6 bg-gradient-to-br from-green-500 to-blue-600 rounded-full flex items-center justify-center animate-pulse">
            <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" /></svg>
          </div>
          <span className="text-gray-700 dark:text-gray-300 font-medium">{statusMessage}</span>
        </div>
        <div className="w-full bg-gray-200 dark:bg-gray-600 rounded-full h-2.5 overflow-hidden">
          <div className="bg-gradient-to-r from-blue-600 to-purple-600 h-2.5 rounded-full transition-all duration-300 ease-linear" style={{ width: `${progress}%` }}></div>
        </div>
        <div className="text-right"><span className="text-sm text-gray-500 dark:text-gray-400">{progress}%</span></div>
        {currentTip && (
          <div className="pt-2 border-t border-gray-200 dark:border-gray-600">
            <div className="flex items-start space-x-2 text-xs text-gray-600 dark:text-gray-400">
              <svg className="w-4 h-4 flex-shrink-0 mt-0.5 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
              <span className="flex-1 leading-relaxed">{currentTip}</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Streaming Bubble
const StreamingBubble = ({ text }) => (
  <div className="flex justify-start">
    <div className="max-w-[85%] md:max-w-3xl order-1">
      <div className="flex items-center space-x-2 mb-2">
        <div className="w-6 h-6 bg-gradient-to-br from-green-500 to-blue-600 rounded-full flex items-center justify-center">
          <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" /></svg>
        </div>
        <span className="text-sm font-medium text-gray-900 dark:text-white">DataWeb AI</span>
        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400">
          <span className="w-1.5 h-1.5 bg-green-500 rounded-full mr-1 animate-pulse"></span>Streaming
        </span>
      </div>
      <div className="px-4 py-3 rounded-2xl shadow-sm text-sm md:text-base bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 text-gray-900 dark:text-white rounded-bl-md">
        {text ? (
          <><MarkdownRenderer content={text} /><span className="inline-block w-0.5 h-4 bg-blue-500 animate-pulse ml-0.5 align-text-bottom"></span></>
        ) : (
          <div className="flex items-center space-x-2 py-1">
            <div className="flex space-x-1">
              <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
              <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
              <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
            </div>
            <span className="text-sm text-gray-500 dark:text-gray-400">Generating response...</span>
          </div>
        )}
      </div>
    </div>
  </div>
);

// ── Main Component ──
const StreamlinedChatApp = ({
  message, setMessage, messages, isTyping, wsConnected, wsConnecting,
  onSendMessage, error, typingText, onLogout, reportUrl, onRestart,
  currentStatus, agentStatus, processingStage, isLoading, sessionId, userName,
  requestUrlRefresh, registerUrlRefreshCallback,
  // Reports
  savedReports, savedReportContent, isLoadingReports, onLoadSavedReports, onViewSavedReport, onDeleteReport,
  // Sessions
  savedSessions, isLoadingSessions, onLoadSessions, onLoadSession, onDeleteSession,
  onRequestNewSession,
}) => {
  // Panel state (mutually exclusive left panels)
  const [isChatHistoryOpen, setIsChatHistoryOpen] = useState(false);
  const [isReportsPanelOpen, setIsReportsPanelOpen] = useState(false);

  // Report viewer (right side)
  const [isReportExpanded, setIsReportExpanded] = useState(false);
  const [reportToShow, setReportToShow] = useState('');

  // Other state
  const [showSuggestions, setShowSuggestions] = useState(true);
  const [starterQuestions, setStarterQuestions] = useState([]);
  const [researchSources, setResearchSources] = useState([]);
  const [loadingReports, setLoadingReports] = useState(new Set());
  const [authError, setAuthError] = useState(null);

  const chatEndRef = useRef(null);
  const textareaRef = useRef(null);
  const reportContentRef = useRef(null);


  // Mutual exclusivity for left panels
  const handleToggleChatHistory = useCallback(() => {
    setIsChatHistoryOpen(prev => {
      const next = !prev;
      if (next) setIsReportsPanelOpen(false);
      return next;
    });
  }, []);

  const handleToggleReports = useCallback(() => {
    setIsReportsPanelOpen(prev => {
      const next = !prev;
      if (next) setIsChatHistoryOpen(false);
      return next;
    });
  }, []);

  // Sessions
  const handleRequestSessions = useCallback(() => {
    if (onLoadSessions) onLoadSessions();
  }, [onLoadSessions]);

  const handleSelectSession = useCallback((sid) => {
    if (onLoadSession) onLoadSession(sid);
    setIsChatHistoryOpen(false);
  }, [onLoadSession]);

  // Reports from sidebar → open on right
  const handleViewReportFromSidebar = useCallback((reportSessionId) => {
    if (onViewSavedReport) {
      setReportToShow('Loading report content...');
      setIsReportExpanded(true);
      setIsReportsPanelOpen(false);
      onViewSavedReport(reportSessionId);
    }
  }, [onViewSavedReport]);

  // When report content arrives via WS, show it
  useEffect(() => {
    if (savedReportContent) {
      if (savedReportContent.content) {
        setReportToShow(savedReportContent.content);
        setIsReportExpanded(true);
      } else if (savedReportContent.error) {
        setReportToShow(`# Error\n\n${savedReportContent.error}`);
        setIsReportExpanded(true);
      }
    }
  }, [savedReportContent]);

  // Starter questions
  const initialQuestions = useMemo(() => getRandomQuestions(4), []);
  useEffect(() => {
    if (starterQuestions.length === 0) setStarterQuestions(initialQuestions);
  }, [initialQuestions, starterQuestions.length]);
  const refreshStarterQuestions = useCallback(() => setStarterQuestions(getRandomQuestions(4)), []);

  // Auto-scroll
  useEffect(() => {
    const t = setTimeout(() => {
      if (messages.length > 0 || isLoading || isTyping) chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, 100);
    return () => clearTimeout(t);
  }, [messages.length, messages, isLoading, isTyping]);

  // Auto-resize textarea
  useEffect(() => {
    if (textareaRef.current && message) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = `${Math.min(textareaRef.current.scrollHeight, 120)}px`;
    }
  }, [message]);

  // Report from inline tag
  const handleOpenReportFromTag = useCallback(async (reportData) => {
    const isExpired = reportData.expiresAt && new Date() > new Date(reportData.expiresAt);
    if (reportData.url && !isExpired) { await openReportWithUrl(reportData); return; }
    if (reportData.s3Key && onViewSavedReport) {
      const sessionMatch = reportData.s3Key.match(/(session_[^_]+_[^_]+)/);
      if (sessionMatch) {
        setReportToShow('Loading report content...');
        setIsReportExpanded(true);
        onViewSavedReport(sessionMatch[1]);
        return;
      }
    }
    if (!reportData.s3Key) {
      setReportToShow('# Error\n\nNo S3 key available.'); setIsReportExpanded(true); return;
    }
    const reportId = reportData.id || reportData.s3Key;
    setLoadingReports(prev => new Set(prev).add(reportId));
    setReportToShow('Loading report content...'); setIsReportExpanded(true);
    try {
      const urlData = await getPresignedUrl(reportData.s3Key, userName, sessionId);
      const response = await fetch(urlData.url, { method: 'GET', headers: { 'Accept': 'text/plain, text/markdown, */*' }, cache: 'no-cache', mode: 'cors' });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const content = await response.text();
      setReportToShow(content?.trim() || '# Report Not Ready\n\nTry again.');
    } catch (err) {
      setReportToShow(`# Error Loading Report\n\n**Error:** ${err.message}`);
    } finally {
      setLoadingReports(prev => { const n = new Set(prev); n.delete(reportId); return n; });
    }
  }, [userName, sessionId, onViewSavedReport]);

  const openReportWithUrl = useCallback(async (reportData) => {
    try {
      setReportToShow('Loading report content...'); setIsReportExpanded(true);
      const response = await fetch(reportData.url, {
        method: 'GET', headers: { 'Accept': 'text/plain, text/markdown, */*', 'Cache-Control': 'no-cache' }, cache: 'no-cache', mode: 'cors'
      });
      if (!response.ok) {
        if (response.status === 403) { await handleOpenReportFromTag(reportData); return; }
        throw new Error(`HTTP ${response.status}`);
      }
      const content = await response.text();
      setReportToShow(content?.trim() || '# Report Content Unavailable');
    } catch (err) {
      if (err.message === 'URL_EXPIRED') await handleOpenReportFromTag(reportData);
      else setReportToShow(`# Error Loading Report\n\n**Error:** ${err.message}`);
    }
  }, [handleOpenReportFromTag]);

  const handleCloseReport = useCallback(() => { setIsReportExpanded(false); setReportToShow(''); }, []);
  const handleSuggestionClick = useCallback((s) => onSendMessage(s), [onSendMessage]);

  // Filtered messages
  const filteredMessages = useMemo(() => filterStatusMessages(messages), [messages]);
  const { completedMessages, activeStreamingMessage } = useMemo(() => {
    const completed = []; let streaming = null;
    for (const msg of filteredMessages) { if (msg.isStreaming) streaming = msg; else completed.push(msg); }
    return { completedMessages: completed, activeStreamingMessage: streaming };
  }, [filteredMessages]);

  const stableWebSocketHandler = useCallback((msg) => { if (typeof onSendMessage === 'function') onSendMessage(msg); }, [onSendMessage]);
  const showLoadingIndicator = isLoading && !activeStreamingMessage;
  const showStreamingBubble = activeStreamingMessage != null;
  const isLeftPanelOpen = isChatHistoryOpen || isReportsPanelOpen;

  return (
    <div className="flex flex-col h-screen bg-gray-50 dark:bg-gray-900 overflow-hidden">
      {/* Left Sidebars */}
      <ChatHistorySidebar
        isOpen={isChatHistoryOpen}
        onClose={() => setIsChatHistoryOpen(false)}
        sessionId={sessionId}
        userName={userName}
        sessions={savedSessions}
        isLoading={isLoadingSessions}
        onRequestSessions={handleRequestSessions}
        onSelectSession={handleSelectSession}
        onDeleteSession={onDeleteSession}
      />
      <ReportsSidebar
        isOpen={isReportsPanelOpen}
        onClose={() => setIsReportsPanelOpen(false)}
        reports={savedReports}
        isLoading={isLoadingReports}
        onLoadReports={onLoadSavedReports}
        onViewReport={handleViewReportFromSidebar}
        onDeleteReport={onDeleteReport} 
      />

      {/* Header */}
      <div className={`flex-shrink-0 z-30 ${isLeftPanelOpen ? 'md:ml-[360px]' : 'ml-0'} transition-all duration-300`}>
        <ChatHeader
          wsConnected={wsConnected}
          wsConnecting={wsConnecting}
          isChatHistoryOpen={isChatHistoryOpen}
          isReportsPanelOpen={isReportsPanelOpen}
          onToggleChatHistory={handleToggleChatHistory}
          onToggleReports={handleToggleReports}
          onRestart={onRestart}
          onLogout={onLogout}
        />
      </div>

      {/* Main content */}
      <div className={`flex flex-1 overflow-hidden ${isLeftPanelOpen ? 'md:ml-[360px]' : 'ml-0'} transition-all duration-300`}>
        <div className="flex-1 min-w-0 flex flex-col">
          {/* Connection banner */}
          {(!wsConnected || wsConnecting || error || authError) && (
            <div className="px-4 md:px-6 py-2 flex-shrink-0">
              <div className={`px-4 py-2 rounded-lg text-sm ${
                authError ? 'bg-orange-50 border border-orange-200 text-orange-800 dark:bg-orange-900/20 dark:border-orange-800 dark:text-orange-400'
                : error ? 'bg-red-50 border border-red-200 text-red-800 dark:bg-red-900/20 dark:border-red-800 dark:text-red-400'
                : wsConnecting ? 'bg-yellow-50 border border-yellow-200 text-yellow-800 dark:bg-yellow-900/20 dark:border-yellow-800 dark:text-yellow-400'
                : 'bg-red-50 border border-red-200 text-red-800 dark:bg-red-900/20 dark:border-red-800 dark:text-red-400'
              }`}>
                <div className="flex items-center space-x-2">
                  <div className={`w-2 h-2 rounded-full ${authError ? 'bg-orange-400' : error ? 'bg-red-400' : wsConnecting ? 'bg-yellow-400 animate-pulse' : 'bg-red-400'}`}></div>
                  <span>{authError ? `Auth error: ${authError}` : error || (wsConnecting ? 'Connecting to DataWeb AI...' : 'Connection lost. Reconnecting...')}</span>
                </div>
              </div>
            </div>
          )}

          {/* Messages */}
          <div className="flex-1 overflow-y-auto px-4 md:px-6 py-4 md:py-6">
            {completedMessages.length === 0 && !isLoading && !isTyping && !showStreamingBubble ? (
              <EmptyState starterQuestions={starterQuestions} refreshStarterQuestions={refreshStarterQuestions} onSuggestionClick={handleSuggestionClick} wsConnected={wsConnected} />
            ) : (
              <div className="max-w-4xl mx-auto space-y-4 md:space-y-6">
                {completedMessages.map((msg, index) => (
                  <div key={msg.id || index} className={`flex ${msg.isUser ? 'justify-end' : 'justify-start'}`}>
                    <div className={`max-w-[85%] md:max-w-3xl ${msg.isUser ? 'order-2' : 'order-1'}`}>
                      {!msg.isUser && (
                        <div className="flex items-center space-x-2 mb-2">
                          <div className="w-6 h-6 bg-gradient-to-br from-green-500 to-blue-600 rounded-full flex items-center justify-center">
                            <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" /></svg>
                          </div>
                          <span className="text-sm font-medium text-gray-900 dark:text-white">DataWeb AI</span>
                        </div>
                      )}
                      <div className={`px-4 py-3 rounded-2xl shadow-sm text-sm md:text-base ${
                        msg.isUser ? 'bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-br-md'
                        : 'bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 text-gray-900 dark:text-white rounded-bl-md'
                      }`}>
                        <MessageRenderer message={msg} index={index} messages={completedMessages} showSuggestions={showSuggestions} isTyping={isTyping} isLoading={isLoading} onSuggestionClick={handleSuggestionClick} onOpenReport={handleOpenReportFromTag} wsConnected={wsConnected} loadingReports={loadingReports} requestUrlRefresh={requestUrlRefresh} sonnetSendMessage={stableWebSocketHandler} />
                      </div>
                      {msg.isUser && (
                        <div className="flex items-center justify-end space-x-2 mt-2">
                          <span className="text-sm font-medium text-gray-900 dark:text-white">You</span>
                          <div className="w-6 h-6 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center text-white text-xs font-bold">
                            {userName ? userName.charAt(0).toUpperCase() : 'U'}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
                {showStreamingBubble && <StreamingBubble text={activeStreamingMessage.text} />}
                {showLoadingIndicator && <SimpleLoadingIndicator message="DataWeb AI is thinking..." agentStatus={agentStatus} currentStatus={currentStatus} />}
                {isTyping && !showStreamingBubble && !showLoadingIndicator && <SimpleLoadingIndicator message="DataWeb AI is thinking..." agentStatus={agentStatus} currentStatus={currentStatus} />}
                <div ref={chatEndRef} />
              </div>
            )}
          </div>

          <ChatInput message={message} setMessage={setMessage} onSendMessage={onSendMessage} wsConnected={wsConnected} wsConnecting={wsConnecting} isLoading={isLoading} isTyping={isTyping} textareaRef={textareaRef} />
        </div>

        {/* Report Panel (RIGHT SIDE) */}
        {isReportExpanded && (
          <>
            <div className="fixed inset-0 bg-black/50 z-40 md:hidden transition-opacity" onClick={handleCloseReport} />
            <div className="fixed inset-0 z-50 md:relative md:inset-auto md:z-auto md:w-1/2 md:min-w-[400px] md:max-w-[800px] bg-white dark:bg-gray-800 flex flex-col flex-shrink-0 md:border-l border-gray-200 dark:border-gray-700">
              {/* Report header */}
              <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700 bg-gradient-to-r from-blue-50 to-purple-50 dark:from-blue-900/20 dark:to-purple-900/20 flex-shrink-0">
                <div className="flex items-center space-x-3">
                  <div className="w-8 h-8 bg-gradient-to-br from-green-500 to-blue-600 rounded-lg flex items-center justify-center shadow-md">
                    <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                  </div>
                  <div>
                    <h3 className="text-base md:text-lg font-semibold text-gray-900 dark:text-white">Research Report</h3>
                    <p className="text-xs md:text-sm text-gray-500 dark:text-gray-400">AI-generated analysis and insights</p>
                  </div>
                </div>

                <div className="flex items-center space-x-2">
                  {reportToShow && !reportToShow.startsWith('# Error') && !reportToShow.includes('Loading') && (
                    <>
                      <PDFDownloadButton
                        content={reportToShow}
                        contentRef={reportContentRef}
                        filename={`DataWeb_Report_${sessionId || 'report'}.pdf`}
                      />
                      <ShareButton
                        content={reportToShow}
                        title="DataWeb AI Report"
                        text="Check out this AI-generated data analysis report"
                      />
                      <button
                        onClick={() => { navigator.clipboard.writeText(reportToShow); }}
                        className="hidden sm:flex p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                        title="Copy report to clipboard"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                        </svg>
                      </button>
                    </>
                  )}

                  <button
                    onClick={handleCloseReport}
                    className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                    title="Close report panel"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              </div>

              {/* Report content */}
              <div className="flex-1 overflow-y-auto p-4 md:p-6">
                <div className="max-w-none" ref={reportContentRef}>
                  {reportToShow.includes('Loading') ? (
                    <div className="flex items-center justify-center py-8">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mr-3" />
                      <span className="text-gray-600 dark:text-gray-400">
                        Loading report content...
                      </span>
                    </div>
                  ) : (
                    renderContentWithCharts(reportToShow)
                  )}
                </div>
              </div>
            </div>
          </>
        )}
      </div>

      <SourceTracker sources={researchSources} isVisible={completedMessages.length > 0} />

      {/* Custom CSS */}
      <style>{`
        @keyframes slideIn {
          from { transform: translateX(100%); opacity: 0; }
          to { transform: translateX(0); opacity: 1; }
        }
        .animate-slideIn { animation: slideIn 0.3s ease-out; }
        @media (max-width: 768px) {
          .animate-slideIn { animation: slideIn 0.25s ease-out; }
        }
        @keyframes bounce {
          0%, 80%, 100% { transform: translateY(0); }
          40% { transform: translateY(-8px); }
        }
        .animate-bounce { animation: bounce 1.4s ease-in-out infinite; }
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(-10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fadeIn { animation: fadeIn 0.3s ease-out; }
      `}</style>
    </div>
  );
};

export default StreamlinedChatApp;